import { useEffect, useMemo, useState } from "react";
import { Box, Button, Card, CardContent, Grid, Stack, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import CenteredListCard from "../../../components/List/CenteredListCardLayout";
import ListCardHeader from "../../../components/List/ListCardHeader";
import ResponsivePagination from "../../../components/ResponsivePagination";
import useCachedListQuery from "../../../hooks/useCachedListQuery";
import { useAuth, useLoading, useNavigation, useSnackbar } from "../../../Context/Contexts";
import { BonusSystemViewModel, ListQuery } from "../../../Types/ExportTypes";

const defaultQuery: ListQuery = { page: 0, pageSize: 12, filter: "", filters: [] };

const BonusSystemListPage: React.FC = () => {
  const { t } = useTranslation();
  const { authService } = useAuth();
  const { showSnackbar } = useSnackbar();
  const { showLoading, hideLoading } = useLoading();
  const { navigate } = useNavigation();

  const [query, setQuery] = useCachedListQuery(defaultQuery);
  const [systems, setSystems] = useState<BonusSystemViewModel[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [filter, setFilter] = useState("");

  const isFiltering = useMemo(() => filter.trim().length > 0, [filter]);

  const fetchSystems = async () => {
    showLoading();
    try {
      const response = await authService.post<{ items: BonusSystemViewModel[]; count: number }>("/bonusApi/BonusSystemList", query);
      setSystems(response.items ?? []);
      setTotalCount(response.count ?? 0);
    } catch (error: any) {
      showSnackbar(error.response?.data ?? error.message, "error");
    } finally {
      hideLoading();
    }
  };

  useEffect(() => {
    fetchSystems();
  }, [query]);

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setQuery({ ...query, filter, page: 0 });
  };

  const handleSelect = (id: number) => {
    navigate({ path: "/sales/bonussystem/edit", params: { id } });
  };

  const handleCreate = () => {
    navigate({ path: "/sales/bonussystem/edit", params: { id: "" } });
  };

  return (
    <CenteredListCard title={t("common.bonusSystem")}>
      <ListCardHeader
        icon={<i className="fas fa-gift" />}
        title=""
      />
      <Box sx={{ marginBottom: 2 }}>
        <Button
          variant="contained"
          color="success"
          size="small"
          onClick={handleCreate}
        >
          {t("common.create")}
        </Button>

      </Box>
      <Box component="form" onSubmit={handleSearchSubmit} sx={{ mb: 2, display: "flex", gap: 1 }}>
        <TextField
          label={t("common.search")}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          size="small"
          fullWidth
        />
        <Button type="submit" variant="contained">
          {t("common.search")}
        </Button>
      </Box>
      <Grid container spacing={2}>
        {systems.map((system) => (
          <Grid item xs={12} md={6} lg={4} key={system.id}>
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardContent>
                <Stack spacing={1}>
                  <Typography variant="h6">{system.description}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {system.bonusGroupDescription || t("common.notSet")}
                  </Typography>
                  <Typography variant="body2" color={system.active ? "success.main" : "text.secondary"}>
                    {system.active ? t("common.active") : t("common.inactive")}
                  </Typography>
                  <Typography variant="body2">
                    {t("common.validDays")}: {system.bonusValidDays}
                  </Typography>
                  <Button variant="contained" onClick={() => handleSelect(system.id)}>
                    {t("common.edit")}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
        {systems.length === 0 && (
          <Grid item xs={12}>
            <Typography align="center">{isFiltering ? t("common.noEntriesFound") : t("common.noEntriesFound")}</Typography>
          </Grid>
        )}
      </Grid>
      <ResponsivePagination
        page={query.page}
        pageSize={query.pageSize}
        totalCount={totalCount}
        onPageChange={(page) => setQuery({ ...query, page })}
      />
    </CenteredListCard>
  );
};

export default BonusSystemListPage;
